<script setup lang="ts">
	export type ProductCardSchema = {
		title: string
		desc: string
		key: string
		category?: string
		[id: string]: unknown
	}

	const {
		circularIcon = true,
		defaultClass = 'bg-white hover:border-success-500 hover:bg-success-25 outline-primary-2 00 light-shadow'
	} = defineProps<{
		content?: ProductCardSchema | null
		circularIcon?: boolean
		defaultClass?: ClassContext
		iconClass?: ClassContext
	}>()
	defineEmits<{
		(event: 'select'): void
	}>()
	const slots = useSlots()
	const { addProductsIconFill } = useUtils()
</script>

<template>
	<button
		type="button"
		name="product-card"
		:aria-label="content?.title"
		:class="[defaultClass, 'product-card relative transition sm:w-full']"
		data-testid="product-card"
		@click="$emit('select')"
	>
		<div class="my-auto sm:w-full sm:px-2">
			<slot>
				<div class="items-start gap-x-2.5 gap-y-3 sm:flex sm:items-center">
					<div
						class="inline-flex h-7 w-7 flex-none items-center justify-center border"
						:class="[circularIcon ? 'rounded-full' : 'rounded-[4px]', iconClass]"
					>
						<slot v-if="!slots?.icon" :name="`icon(${content?.key})`" :content-key="content?.key">
							<lazy-tools-icon
								:dir="addProductsIconFill(content?.category || '', content?.key).dir"
								:icon="addProductsIconFill(content?.category || '', content?.key).name"
								:path-class="addProductsIconFill(content?.category || '', content?.key).pathClass"
								:height="16"
								:width="16"
							/>
						</slot>
						<slot name="icon" :content-key="content?.key" />
					</div>
					<div class="block space-y-2">
						<h4 class="text-sm font-semibold capitalize leading-5 tracking-wide text-gray-600">
							{{ content?.title ?? '' }}
						</h4>
						<p
							class="mt-2 block max-w-[300px] text-sm font-normal leading-relaxed text-gray-500 md:hidden md:max-w-[250px]"
							v-html="content?.desc?.replace(/(₦[0-9, '']+)/, `<span class='font-medium'>$1</span>`)"
						/>
					</div>
				</div>
				<p
					class="hidden pt-3 text-xs font-normal leading-relaxed tracking-wide text-gray-500 md:block"
					v-html="content?.desc?.replace(/(₦[0-9, '']+)/, `<span class='font-medium'>$1</span>`)"
				/>
			</slot>
		</div>
	</button>
</template>

<style scoped>
	.product-card {
		@apply flex min-h-[150px] flex-none cursor-pointer flex-col rounded-xl border p-[16px] text-left sm:h-[174px] md:max-w-[280px];
	}
</style>
